import type {
  PsvLink,
  PsvMarker,
  VirtualTourNode,
} from '@indutrust/factory-model';
import { type } from '@ngrx/signals';
import { eventGroup } from '@ngrx/signals/events';

/**
 * Events driving the {@link import('./virtual-tour.store').VirtualTourStore}.
 *
 * The store loads the full tour graph for a factory (nodes hydrated with
 * their links + markers) and exposes authoring mutations. Every mutation
 * re-emits {@link virtualTourEvents.mutationSucceeded}, which triggers a
 * single reload so the in-memory graph stays consistent with the backend
 * without bespoke optimistic-update bookkeeping.
 *
 * Source namespace `factory.virtual-tour` keeps the Redux DevTools trace
 * grouped with the other factory sub-resources.
 *
 * @example
 * const dispatch = injectDispatch(virtualTourEvents);
 * dispatch.loadRequested({ factoryId: 7 });
 * dispatch.nodeCreateRequested({ factoryId: 7, name: 'Entrance' });
 */
export const virtualTourEvents = eventGroup({
  source: 'factory.virtual-tour',
  events: {
    loadRequested: type<{ factoryId: number }>(),
    loadSucceeded: type<{ factoryId: number; nodes: VirtualTourNode[] }>(),
    loadFailed: type<{ error: string }>(),

    nodeCreateRequested: type<{ factoryId: number; name: string }>(),
    nodeUpdateRequested: type<{
      factoryId: number;
      nodeId: number;
      name: string;
    }>(),
    nodeDeleteRequested: type<{ factoryId: number; nodeId: number }>(),
    panoramaUploadRequested: type<{
      factoryId: number;
      nodeId: number;
      file: File;
    }>(),

    linkCreateRequested: type<{
      factoryId: number;
      nodeId: number;
      link: Omit<PsvLink, 'id'>;
    }>(),
    linkDeleteRequested: type<{
      factoryId: number;
      nodeId: number;
      linkId: number;
    }>(),
    linkUpdateRequested: type<{
      factoryId: number;
      nodeId: number;
      linkId: number;
      link: Omit<PsvLink, 'id'>;
    }>(),

    markerCreateRequested: type<{
      factoryId: number;
      nodeId: number;
      marker: Omit<PsvMarker, 'id'>;
    }>(),
    markerDeleteRequested: type<{
      factoryId: number;
      nodeId: number;
      markerId: number;
    }>(),
    markerUpdateRequested: type<{
      factoryId: number;
      nodeId: number;
      markerId: number;
      marker: Omit<PsvMarker, 'id'>;
    }>(),

    mutationSucceeded: type<{ factoryId: number }>(),
    mutationFailed: type<{ error: string }>(),
  },
});
